import { memo, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CalendarDays, Inbox, Phone, CreditCard, Menu } from 'lucide-react';
import { typedApi } from '../api/typed';

/**
 * MobileTabBar — fixed bottom-of-viewport navigation for screens <768px.
 *
 * High-traffic destinations only: Today / Inbox / Calls / Payments. The
 * "More" tab opens the full sidebar drawer (via parent's `onOpenMore`).
 *
 * Live count badges sourced from `/api/counts` (same payload Sidebar uses).
 * Badges refresh
 * automatically on `polkdesq:data-mutated` broadcasts so the user sees
 * Inbox / Calls / Payments counts decrement after their action without
 * a manual reload.
 *
 * CSS gating + 56px touch targets live in `/app/frontend/src/styles/globals.css`
 * (`.pd-mobile-tabbar`).
 */
const TABS = [
  { path: '/',         label: 'Today',    icon: CalendarDays, countKey: 'tasks' },
  { path: '/inbox',    label: 'Inbox',    icon: Inbox,        countKey: 'inbox' },
  { path: '/calls',    label: 'Calls',    icon: Phone,        countKey: 'calls' },
  { path: '/payments', label: 'Payments', icon: CreditCard,   countKey: 'payments', urgentKey: 'paymentsOverdue' },
];

export const MobileTabBar = memo(function MobileTabBar({ onOpenMore }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [counts, setCounts] = useState({});

  // Pull badge counts from the shared sidebar endpoint. Re-fetch on any
  // cross-app mutation broadcast (debounce handled in views). Aborts any
  // in-flight request on unmount or re-trigger to avoid a stale setState.
  useEffect(() => {
    let alive = true;
    const controllers = new Set();
    const fetchCounts = async () => {
      const controller = new AbortController();
      controllers.add(controller);
      try {
        const data = await typedApi.get('/api/counts', { signal: controller.signal });
        if (alive) setCounts(data || {});
      } catch (err) {
        if (err && (err.name === 'CanceledError' || err.name === 'AbortError')) return;
        /* counts missing is non-fatal */
      } finally {
        controllers.delete(controller);
      }
    };
    fetchCounts();
    const handler = () => fetchCounts();
    window.addEventListener('polkdesq:data-mutated', handler);
    return () => {
      alive = false;
      window.removeEventListener('polkdesq:data-mutated', handler);
      controllers.forEach((c) => c.abort());
      controllers.clear();
    };
  }, []);

  return (
    <nav className="pd-mobile-tabbar" data-testid="mobile-tabbar" aria-label="Primary">
      {TABS.map((t) => {
        const isActive = t.path === '/' ? pathname === '/' : pathname.startsWith(t.path);
        const total = t.countKey ? counts[t.countKey] || 0 : 0;
        const urgent = t.urgentKey ? counts[t.urgentKey] || 0 : 0;
        return (
          <button
            key={t.path}
            type="button"
            data-testid={`mobile-tab-${t.label.toLowerCase()}`}
            data-active={isActive || undefined}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => navigate(t.path)}
            className="pd-mobile-tab"
          >
            <div className="relative">
              <t.icon className="w-5 h-5" />
              {urgent > 0 ? (
                <span
                  data-testid={`mobile-tab-urgent-${t.label.toLowerCase()}`}
                  className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-1 rounded-full text-[8px] font-bold text-white flex items-center justify-center tabular-nums"
                  style={{ background: 'var(--pd-crimson)' }}
                >
                  {urgent > 9 ? '9+' : urgent}
                </span>
              ) : total > 0 ? (
                <span
                  data-testid={`mobile-tab-count-${t.label.toLowerCase()}`}
                  className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-1 rounded-full text-[8px] font-bold flex items-center justify-center tabular-nums"
                  style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-text)', border: '1px solid var(--pd-border)' }}
                >
                  {total > 9 ? '9+' : total}
                </span>
              ) : null}
            </div>
            <span>{t.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        data-testid="mobile-tab-more"
        className="pd-mobile-tab"
        onClick={onOpenMore}
      >
        <Menu className="w-5 h-5" />
        <span>More</span>
      </button>
    </nav>
  );
});
